import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	useMemo,
	useRef,
	ReactNode,
} from "react";
import * as Sentry from "@sentry/react";
import { useAuth } from "./BaseAuthProvider";
import { API_BASE_URL } from "../lib/requestFunctions";

/**
 * Subscription status types matching the User model
 */
export type SubscriptionStatus = 'trial' | 'active' | 'expired' | 'cancelled' | 'past_due';

/**
 * Subscription state exposed by the context
 */
export interface SubscriptionState {
	/** Current subscription status */
	subscriptionStatus: SubscriptionStatus;
	/** When the trial period ends (null if not on trial) */
	trialEndDate: Date | null;
	/** Number of days remaining in trial (0 if not on trial or expired) */
	trialDaysRemaining: number;
	/** Whether user is currently in an active trial */
	isTrialActive: boolean;
	/** Whether user has an active subscription (trial or paid) */
	isSubscriptionActive: boolean;
	/** Whether user can insert content (has active trial or subscription) */
	canInsert: boolean;
	/** Whether subscription data is being loaded */
	loading: boolean;
	/** Error message if subscription check failed */
	error: string | null;
}

interface SubscriptionContextValue extends SubscriptionState {
	/** Manually refresh subscription status from server */
	refresh: () => void;
	/** Open Stripe Checkout and poll for subscription activation */
	openCheckout: (priceTier?: 'annual' | 'monthly') => Promise<void>;
	/** Whether a checkout is currently in progress (window open, polling) */
	checkoutInProgress: boolean;
	/** Open Stripe Customer Portal and poll for subscription changes */
	openCustomerPortal: () => Promise<void>;
	/** Whether the customer portal is currently open (polling for changes) */
	portalInProgress: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

interface SubscriptionProviderProps {
	children: ReactNode;
}

/**
 * Calculate the number of days remaining until a given end date.
 * Returns 0 if the date has passed or is invalid.
 */
function calculateDaysRemaining(endDate: Date | null): number {
	if (!endDate) {
		return 0;
	}

	const now = new Date();
	const diffMs = endDate.getTime() - now.getTime();

	if (diffMs <= 0) {
		return 0;
	}

	// Convert milliseconds to days, rounding up so any partial day
	// counts as a full day (e.g. signup day shows "7 days remaining")
	const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
	return Math.max(diffDays, 1);
}

/**
 * Parse an ISO date string into a Date object.
 * Returns null if the string is undefined or invalid.
 */
function parseDate(dateString?: string): Date | null {
	if (!dateString) {
		return null;
	}

	const date = new Date(dateString);

	// Check for invalid date
	if (isNaN(date.getTime())) {
		return null;
	}

	return date;
}

/**
 * SubscriptionProvider component that manages subscription state.
 * Derives subscription information from the authenticated user.
 */
export function SubscriptionProvider({
	children,
}: SubscriptionProviderProps) {
	const { user, isLoading: authLoading, isAuthenticated, getToken, refreshUser, authenticatedFetch } = useAuth();

	const [checkoutInProgress, setCheckoutInProgress] = useState(false);
	const [portalInProgress, setPortalInProgress] = useState(false);
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const windowCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
	// Snapshot of subscription state before opening the portal,
	// so we can detect when anything changes (cancel, reactivate, etc.)
	const prePortalSnapshotRef = useRef<{
		cancelAtPeriodEnd: boolean;
		subscriptionStatus: SubscriptionStatus;
	} | null>(null);

	/**
	 * Derive subscription state synchronously from user data via useMemo.
	 * This ensures the subscription state is always in sync with the user —
	 * no lag from useEffect chains that could miss updates.
	 */
	const state: SubscriptionState = useMemo(() => {
		if (authLoading) {
			return {
				subscriptionStatus: 'trial' as SubscriptionStatus,
				trialEndDate: null,
				trialDaysRemaining: 0,
				isTrialActive: false,
				isSubscriptionActive: false,
				canInsert: false,
				loading: true,
				error: null,
			};
		}

		if (!isAuthenticated || !user) {
			return {
				subscriptionStatus: 'trial' as SubscriptionStatus,
				trialEndDate: null,
				trialDaysRemaining: 0,
				isTrialActive: false,
				isSubscriptionActive: false,
				canInsert: false,
				loading: false,
				error: null,
			};
		}

		const trialEndDate = parseDate(user.trialEndDate);
		const trialDaysRemaining = calculateDaysRemaining(trialEndDate);
		const subscriptionStatus = user.subscriptionStatus;
		const isTrialActive = subscriptionStatus === 'trial' && trialDaysRemaining > 0;
		const isSubscriptionActive = subscriptionStatus === 'active' || isTrialActive;
		const canInsert = isSubscriptionActive;

		return {
			subscriptionStatus,
			trialEndDate,
			trialDaysRemaining,
			isTrialActive,
			isSubscriptionActive,
			canInsert,
			loading: false,
			error: null,
		};
	}, [user, authLoading, isAuthenticated]);

	/**
	 * Refresh subscription by re-fetching user data from the server.
	 */
	const refresh = useCallback(() => {
		refreshUser();
	}, [refreshUser]);

	/**
	 * Stop all polling intervals and mark checkout as done.
	 */
	const stopPolling = useCallback(() => {
		if (pollingRef.current) {
			clearInterval(pollingRef.current);
			pollingRef.current = null;
		}
		if (windowCheckRef.current) {
			clearInterval(windowCheckRef.current);
			windowCheckRef.current = null;
		}
	}, []);

	/**
	 * Open Stripe Checkout in a new browser window and poll for activation.
	 * Calls POST /api/subscription/checkout, opens the returned URL,
	 * then polls refreshUser every 3s until subscription becomes active (max 5 min).
	 * Also watches for the checkout window closing to trigger an immediate refresh.
	 */
	const openCheckout = useCallback(async (priceTier: 'annual' | 'monthly' = 'annual') => {
		const token = getToken();
		if (!token) return;

		// If a previous session is still polling, stop it before starting a new one
		stopPolling();
		setCheckoutInProgress(true);

		try {
			const response = await authenticatedFetch(`${API_BASE_URL}/subscription/checkout`, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ priceTier }),
			});

			if (!response.ok) {
				throw new Error("Failed to create checkout session");
			}

			const data = await response.json();

			// Server found an existing active subscription in Stripe and synced it
			if (data.alreadySubscribed) {
				await refreshUser();
				setCheckoutInProgress(false);
				return;
			}

			Sentry.metrics.count('checkout_initiated', 1);
			const checkoutWindow = window.open(data.url, "_blank");

			// Watch for the checkout window closing to trigger an immediate refresh.
			// This makes the UI update faster than waiting for the next 3s poll.
			if (checkoutWindow) {
				windowCheckRef.current = setInterval(() => {
					if (checkoutWindow.closed) {
						if (windowCheckRef.current) {
							clearInterval(windowCheckRef.current);
							windowCheckRef.current = null;
						}
						// Stop polling and reset state so the user can try again
						stopPolling();
						setCheckoutInProgress(false);
						refreshUser();
					}
				}, 500);
			}

			// Poll for subscription activation (every 5s, up to 1 minute)
			const maxPolls = 12;
			let pollCount = 0;

			pollingRef.current = setInterval(async () => {
				pollCount++;
				await refreshUser();

				if (pollCount >= maxPolls) {
					stopPolling();
					setCheckoutInProgress(false);
				}
			}, 5000);
		} catch (error) {
			Sentry.captureException(error, { extra: { operation: 'open-checkout' } });
			console.error("[Subscription] Checkout error:", error);
			setCheckoutInProgress(false);
		}
	}, [getToken, refreshUser, stopPolling, authenticatedFetch]);

	/**
	 * Open Stripe Customer Portal in a new browser window and poll for changes.
	 * Calls POST /api/subscription/portal, opens the returned URL,
	 * then polls refreshUser every 5s for up to 1 minute.
	 * Also watches for the portal window closing to trigger an immediate refresh.
	 */
	const openCustomerPortal = useCallback(async () => {
		const token = getToken();
		if (!token) return;

		// If a previous session is still polling, stop it before opening portal
		stopPolling();

		// Snapshot current state so we can detect changes on return
		prePortalSnapshotRef.current = {
			cancelAtPeriodEnd: user?.cancelAtPeriodEnd ?? false,
			subscriptionStatus: state.subscriptionStatus,
		};
		setPortalInProgress(true);

		try {
			const response = await authenticatedFetch(`${API_BASE_URL}/subscription/portal`, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
			});

			if (!response.ok) {
				throw new Error("Failed to open subscription portal");
			}

			const data = await response.json();
			const portalWindow = window.open(data.url, "_blank");

			if (portalWindow) {
				windowCheckRef.current = setInterval(() => {
					if (portalWindow.closed) {
						if (windowCheckRef.current) {
							clearInterval(windowCheckRef.current);
							windowCheckRef.current = null;
						}
						refreshUser();
					}
				}, 500);
			}

			const maxPolls = 12;
			let pollCount = 0;

			pollingRef.current = setInterval(async () => {
				pollCount++;
				await refreshUser();

				if (pollCount >= maxPolls) {
					stopPolling();
					setPortalInProgress(false);
				}
			}, 5000);
		} catch (error) {
			Sentry.captureException(error, { extra: { operation: 'open-customer-portal' } });
			console.error("[Subscription] Portal error:", error);
			setPortalInProgress(false);
		}
	}, [getToken, refreshUser, stopPolling, authenticatedFetch, user, state.subscriptionStatus]);

	// Stop polling when subscription status changes during checkout or portal
	useEffect(() => {
		if (state.loading) return;

		if (checkoutInProgress && state.isSubscriptionActive) {
			Sentry.metrics.count('checkout_completed', 1);
			stopPolling();
			setCheckoutInProgress(false);
		}

		// For portal: detect ANY change from the pre-portal snapshot
		// (cancel-at-period-end keeps status "active", so we compare fields directly)
		if (portalInProgress && prePortalSnapshotRef.current) {
			const snap = prePortalSnapshotRef.current;
			const currentCancelAtPeriodEnd = user?.cancelAtPeriodEnd ?? false;
			if (
				state.subscriptionStatus !== snap.subscriptionStatus ||
				currentCancelAtPeriodEnd !== snap.cancelAtPeriodEnd
			) {
				stopPolling();
				setPortalInProgress(false);
				prePortalSnapshotRef.current = null;
			}
		}
	}, [checkoutInProgress, portalInProgress, state.isSubscriptionActive, state.subscriptionStatus, state.loading, user?.cancelAtPeriodEnd, stopPolling]);

	/**
	 * Refresh user data when the taskpane regains focus during checkout.
	 *
	 * Office Add-in taskpanes run in a WebView that throttles/suspends
	 * setInterval timers when the user switches to another window (e.g.
	 * the Stripe checkout in a browser). So the 3s polling stops while
	 * the user is on Stripe's site.
	 *
	 * This effect catches the moment the user returns to Word by
	 * listening to both 'focus' and 'visibilitychange' events.
	 * The server's /api/auth/me endpoint has a syncSubscriptionFromStripe
	 * fallback, so a single refreshUser() call is enough to pick up
	 * a completed checkout even if the webhook hasn't arrived yet.
	 */
	useEffect(() => {
		if (!checkoutInProgress && !portalInProgress) return;

		const handleReturn = () => {
			if (
				document.visibilityState === "visible" ||
				document.hasFocus()
			) {
				console.log("[Subscription] Taskpane regained focus during checkout/portal, refreshing...");
				refreshUser();
			}
		};

		window.addEventListener("focus", handleReturn);
		document.addEventListener("visibilitychange", handleReturn);

		return () => {
			window.removeEventListener("focus", handleReturn);
			document.removeEventListener("visibilitychange", handleReturn);
		};
	}, [checkoutInProgress, portalInProgress, refreshUser]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			stopPolling();
		};
	}, [stopPolling]);

	// Memoize the context value to prevent unnecessary re-renders
	const value: SubscriptionContextValue = useMemo(() => ({
		...state,
		refresh,
		openCheckout,
		checkoutInProgress,
		openCustomerPortal,
		portalInProgress,
	}), [state, refresh, openCheckout, checkoutInProgress, openCustomerPortal, portalInProgress]);

	return (
		<SubscriptionContext.Provider value={value}>
			{children}
		</SubscriptionContext.Provider>
	);
}

/**
 * Hook to access subscription context.
 * Must be used within a SubscriptionProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { canInsert, trialDaysRemaining, isTrialActive } = useSubscription();
 *
 *   if (!canInsert) {
 *     return <UpgradePrompt />;
 *   }
 *
 *   return <InsertButton />;
 * }
 * ```
 */
export function useSubscription(): SubscriptionContextValue {
	const context = useContext(SubscriptionContext);
	if (!context) {
		throw new Error(
			"useSubscription must be used within a SubscriptionProvider",
		);
	}
	return context;
}
